import React from 'react';
import { Music, Volume2 } from 'lucide-react';
import AccessLevelBadge from './AccessLevelBadge';

const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Presentational catalog list. Always calls onSelectTrack, even for locked
 * rows — the play-vs-modal decision is centralized in the parent (Player.jsx).
 */
const PlayerTrackList = ({ tracks, currentIndex, isPlaying, isAuthenticated, hasActiveSubscription, trackDurations, onSelectTrack }) => {
    return (
        <div className="glass-card p-2 sm:p-3 max-h-[420px] sm:max-h-[480px] overflow-y-auto">
            {tracks.map((track, index) => {
                const isCurrent = index === currentIndex;
                // track.duration is often null in the DB for older uploads — once a track has
                // actually been loaded/played, its real <audio> duration (cached by the parent) wins.
                const displayDuration = trackDurations?.[track.id] || track.duration;
                return (
                    <button
                        key={track.id}
                        onClick={() => onSelectTrack(track, index)}
                        className={`w-full flex items-center gap-3 p-2 sm:p-2.5 rounded-lg text-left transition-colors ${
                            isCurrent ? 'bg-purple-600/15 border-l-2 border-purple-500' : 'border-l-2 border-transparent hover:bg-white/5'
                        }`}
                    >
                        <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-900">
                            {track.cover_image_url ? (
                                <img
                                    src={track.cover_image_url}
                                    alt={track.title || 'Cover art'}
                                    width="48"
                                    height="48"
                                    className="w-full h-full object-cover"
                                    onContextMenu={(e) => e.preventDefault()}
                                />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center">
                                    <Music className="w-5 h-5 text-purple-300/60" />
                                </div>
                            )}
                            <AccessLevelBadge
                                accessLevel={track.access_level}
                                iconOnly
                                position="top-right"
                                isAuthenticated={isAuthenticated}
                                hasActiveSubscription={hasActiveSubscription}
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${isCurrent ? 'text-purple-300' : 'text-white'}`}>
                                {track.title || track.original_filename || 'Untitled'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                                {track.artist || track.display_name || 'Unknown Artist'} · {(track.play_count || 0).toLocaleString()} plays
                            </p>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {isCurrent && isPlaying && (
                                <Volume2 className="w-3.5 h-3.5 text-purple-400 animate-pulse" />
                            )}
                            <span className="text-xs text-gray-500 tabular-nums">
                                {formatDuration(displayDuration)}
                            </span>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

export default PlayerTrackList;
